import React, { useLayoutEffect, useEffect } from 'react'

declare global {
  interface Window {
    shiftPressed?: boolean
  }
}

const cleanUpFocusAttrs = (el: HTMLElement) => {
  if (el.dataset.tabIndexZero) {
    el.tabIndex = 0
  } else {
    el.removeAttribute('tabindex')
  }
  el.removeAttribute('data-trapping-enabled')
  el.removeAttribute('data-tab-index-zero')
}

const resetTabIndices = () => {
  global.document
    .querySelectorAll('[data-trapping-enabled="true"]')
    .forEach((element) => cleanUpFocusAttrs(element as HTMLElement))
}

const adjustTabIndices = (wrapper: HTMLElement, exceptionSelector = '') => {
  let exceptions: HTMLElement[] = []

  if (exceptionSelector) {
    exceptions = Array.from(global.document.querySelectorAll(exceptionSelector))
  }
  global.document
    .querySelectorAll(
      'a:not([tabindex="0"]), path:not([tabindex="0"]), g:not([tabindex="0"]), button:not([tabindex="0"]), select:not([tabindex="0"]), input:not([tabindex="0"]), textarea:not([tabindex="0"]), [contenteditable="true"]:not([tabindex="0"])'
    )
    .forEach((el: Element) => {
      const element = el as HTMLElement
      if (!exceptions.includes(element)) {
        element.dataset.trappingEnabled = 'true'
        element.tabIndex = -1
      }
    })
  global.document.querySelectorAll('[tabindex="0"]').forEach((el) => {
    const element = el as HTMLElement
    element.dataset.trappingEnabled = 'true'
    element.dataset.tabIndexZero = 'true'
    element.tabIndex = -1
  })
  wrapper
    .querySelectorAll('[data-trapping-enabled="true"]')
    .forEach((element) => cleanUpFocusAttrs(element as HTMLElement))
}

interface ComponentProps {
  children?: React.ReactChildren | JSX.Element
  exceptionSelector?: string
  wrapperRef: React.RefObject<HTMLElement>
}

const TrapFocus = ({ exceptionSelector, wrapperRef }: ComponentProps) => {
  window.shiftPressed = false

  useEffect(() => {
    const onKeyDown = (ev: KeyboardEvent) => {
      const modalInner: HTMLElement | null = wrapperRef.current
      if (ev.key === 'Shift') {
        window.shiftPressed = true
      }
      if (modalInner) {
        const tabbableElementsInModal = modalInner.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const firstElement = tabbableElementsInModal[0] as HTMLElement
        const lastElement = tabbableElementsInModal[
          tabbableElementsInModal.length - 1
        ] as HTMLElement
        if (ev.key === 'Tab') {
          if (ev.target === firstElement && window.shiftPressed) {
            lastElement.focus()
          } else if (ev.target === lastElement) {
            firstElement.focus()
          }
        }
      }
    }
    const onKeyUp = (ev: KeyboardEvent) => {
      if (ev.key === 'Shift') {
        window.shiftPressed = false
      }
    }
    global.document.body.addEventListener('keydown', onKeyDown)
    global.document.body.addEventListener('keyup', onKeyUp)

    return () => {
      global.document.body.removeEventListener('keydown', onKeyDown)
      global.document.body.removeEventListener('keyup', onKeyUp)
    }
  }, [wrapperRef])
  useLayoutEffect(() => {
    const trapFocusScopeElement: HTMLElement | null = wrapperRef.current
    if (trapFocusScopeElement) {
      adjustTabIndices(trapFocusScopeElement, exceptionSelector)
    }

    return () => {
      resetTabIndices()
    }
  })

  return null
}

export default TrapFocus
